/**
 * Composants du design system SnowChain (repris du document de concept) :
 * HUDPanel, StatTile, Badge, Button, IconButton, PlayerBadge, EventNotice,
 * ResourceCard. Variante « inverse » (texte clair sur verre sombre).
 */
import type { ComponentChildren } from 'preact';
import { CircleCheck, Info, OctagonAlert, TrendingDown, TrendingUp, TriangleAlert, type LucideIcon } from 'lucide-preact';
import type { AlertLevel } from '@snowchain/shared';
import type { AnyIcon } from './icons';

export function HudPanel(props: { title?: string; icon?: AnyIcon; actions?: ComponentChildren; flat?: boolean; class?: string; children?: ComponentChildren; style?: Record<string, string | number> }) {
  const Icon = props.icon;
  return (
    <section class={`sc-hud${props.flat ? ' sc-hud-flat' : ''} ${props.class ?? ''}`} style={props.style}>
      {(props.title || Icon || props.actions) && (
        <div class="sc-hud-head">
          {Icon && <Icon size={14} class="sc-icon" />}
          <span class="sc-hud-title">{props.title}</span>
          {props.actions}
        </div>
      )}
      <div class="sc-hud-body">{props.children}</div>
    </section>
  );
}

export function StatTile(props: { label: string; value: ComponentChildren; unit?: string; delta?: number | null; icon?: AnyIcon; small?: boolean; title?: string }) {
  const Icon = props.icon;
  const d = props.delta;
  return (
    <div class={`sc-stat${props.small ? ' sc-stat-sm' : ''}`} title={props.title}>
      <span class="sc-stat-label">
        {Icon && <Icon size={13} />}
        {props.label}
      </span>
      <span class="sc-stat-value">
        {props.value}
        {props.unit && <small>{props.unit}</small>}
      </span>
      {d !== undefined && d !== null && (
        <span class={`sc-stat-delta ${d > 0 ? 'sc-stat-up' : d < 0 ? 'sc-stat-down' : ''}`}>
          {d > 0 ? <TrendingUp size={12} /> : d < 0 ? <TrendingDown size={12} /> : null}
          {(d > 0 ? '+' : '') + d.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %
        </span>
      )}
    </div>
  );
}

export type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

export function Badge(props: { tone: Tone; pulse?: boolean; children: ComponentChildren }) {
  return (
    <span class={`sc-badge sc-badge-${props.tone}${props.pulse ? ' sc-badge-pulse' : ''}`}>
      <span class="sc-badge-dot" />
      {props.children}
    </span>
  );
}

export function Button(props: {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  full?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: ComponentChildren;
  type?: 'button' | 'submit';
  title?: string;
}) {
  return (
    <button
      type={props.type ?? 'button'}
      class={`sc-btn sc-btn-${props.variant ?? 'primary'} sc-btn-${props.size ?? 'sm'}${props.full ? ' sc-btn-full' : ''}`}
      disabled={props.disabled}
      onClick={props.onClick}
      title={props.title}
    >
      {props.children}
    </button>
  );
}

export function IconButton(props: { icon: LucideIcon; label: string; onClick?: () => void; on?: boolean; size?: 'sm' | 'md' | 'lg'; ghost?: boolean; disabled?: boolean; hotkey?: string }) {
  const Icon = props.icon;
  const size = props.size ?? 'md';
  return (
    <button
      type="button"
      class={`sc-ibtn sc-ibtn-${size}${props.on ? ' sc-ibtn-on' : ''}${props.ghost ? ' sc-ibtn-ghost' : ''}`}
      aria-label={props.label}
      aria-pressed={props.on}
      title={props.hotkey ? `${props.label} (${props.hotkey})` : props.label}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      <Icon size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} />
      {props.hotkey && <span class="sc-ibtn-key">{props.hotkey}</span>}
    </button>
  );
}

const ROLE_LABEL: Record<string, string> = { station: 'Station de ski', supermarche: 'Supermarché', transport: 'Transport', observer: 'Observateur' };
const ROLE_BG: Record<string, string> = { station: 'var(--glacier-4)', supermarche: 'var(--wood-3)', transport: 'var(--night-3)', observer: 'var(--ink-3)' };

export function PlayerBadge(props: { name: string; role: string; online?: boolean; suffix?: string }) {
  const initials = props.name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span class={`sc-pb${props.online !== false ? ' sc-pb-online' : ''}`}>
      <span class="sc-pb-av" style={{ background: ROLE_BG[props.role] ?? 'var(--glacier-4)' }}>
        {initials}
        <span class="sc-pb-dot" />
      </span>
      <span style={{ minWidth: 0 }}>
        <span class="sc-pb-name">{props.name}</span>
        <span class="sc-pb-role">
          {ROLE_LABEL[props.role] ?? props.role}
          {props.suffix ? ` \u00b7 ${props.suffix}` : ''}
        </span>
      </span>
    </span>
  );
}

const LEVEL_ICON: Record<AlertLevel, LucideIcon> = { ok: CircleCheck, warn: TriangleAlert, danger: OctagonAlert, info: Info };

export function EventNotice(props: { level: AlertLevel; title: string; message?: string; meta?: string; onTitle?: () => void; children?: ComponentChildren }) {
  const Icon = LEVEL_ICON[props.level];
  return (
    <div class={`sc-ev sc-ev-${props.level}`}>
      <span class="sc-ev-ic">
        <Icon size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p class="sc-ev-title" onClick={props.onTitle}>
          {props.title}
        </p>
        {props.message && <p class="sc-ev-msg">{props.message}</p>}
        {props.children && <div class="sc-ev-actions">{props.children}</div>}
      </div>
      {props.meta && <span class="sc-ev-meta">{props.meta}</span>}
    </div>
  );
}

export function ToastView(props: { level: AlertLevel; title: string; message?: string }) {
  const Icon = LEVEL_ICON[props.level];
  return (
    <div class={`sc-toast sc-toast-${props.level}`} role="status">
      <span class="sc-toast-ic">
        <Icon size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p class="sc-toast-title">{props.title}</p>
        {props.message && <p class="sc-toast-msg">{props.message}</p>}
      </div>
    </div>
  );
}

export function Meter(props: { ratio: number; tone: Tone }) {
  const color = props.tone === 'danger' ? 'var(--alert-2)' : props.tone === 'warn' ? 'var(--ember-2)' : 'var(--pine-2)';
  return (
    <div class="sc-track">
      <div class="sc-fill" style={{ width: `${Math.max(0, Math.min(1, props.ratio)) * 100}%`, background: color }} />
    </div>
  );
}
